import Widget from "./Widget";
import { gql } from "@apollo/client";

import Text from "@src/lib/ui/Text";
import StudentIcon from "@src/lib/icons/18px/student";
import ContentAvatar, {
  CONTENT_TYPE_COPY,
  LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE,
} from "@src/features/contentLibrary/ContentAvatar";
import { Progress } from "@src/lib/ui/Progress";
import { Badge } from "@src/lib/ui/Badge";
import Link from "next/link";
import {
  RecentlyAssignedLibraryItem__WidgetFragment,
  useRecentlyAssignedLibraryItemsQuery,
} from "./RecentAssignmentsWidget.generated";
import { useMemo } from "react";
import { DateTime } from "luxon";

const RecentAssignmentsWidget: React.FC = () => {
  const sinceDate = useMemo(
    () => DateTime.now().minus({ days: 7 }).toJSDate().toISOString(),
    [],
  );
  const { data } = useRecentlyAssignedLibraryItemsQuery({
    variables: {
      since: sinceDate,
    },
  });
  if (
    data?.RecentlyAssignedLibraryItems.length === 0 ||
    !data?.RecentlyAssignedLibraryItems
  ) {
    return null;
  }
  return (
    <Widget title="Recent Assignments" Icon={StudentIcon}>
      <div className="flex max-h-[300px] w-full flex-col gap-2 overflow-y-auto">
        <div className="sticky top-0 z-20 mb-2 flex items-center border-b border-border bg-background pb-2">
          <div className="flex flex-1">
            <Text variant="fine-print-title" className="text-muted-foreground">
              Assignment
            </Text>
          </div>
          <div className="flex flex-1">
            <Text variant="fine-print-title" className="text-muted-foreground">
              Assigned last 7 days
            </Text>
          </div>
          <div className="flex flex-1 justify-end">
            <Text variant="fine-print-title" className="text-muted-foreground">
              % Completed of Recent Assignments
            </Text>
          </div>
        </div>
        {data?.RecentlyAssignedLibraryItems?.map?.((item) => (
          <AssignmentItem key={item.id} item={item} />
        ))}
      </div>
    </Widget>
  );
};

const AssignmentItem: React.FC<{
  item: RecentlyAssignedLibraryItem__WidgetFragment;
}> = ({ item }) => {
  const {
    numAssignedTotal,
    numCompletedTotal,
    id,
    libraryItem: {
      type,
      name: { en: name },
      coverImage,
    },
    numAssignedSince,
  } = item;
  const progress =
    numAssignedTotal > 0
      ? Math.round((numCompletedTotal / numAssignedTotal) * 100)
      : 0;
  return (
    <Link
      href={{
        pathname: "/library/library-item/[id]",
        query: {
          id,
          mainTab: "manage",
          manageTab: "assignments",
        },
      }}
    >
      <div className="flex flex-row items-center gap-1">
        <div className="flex flex-1 items-center gap-2">
          <ContentAvatar
            defaultBackgroundHashKey={id}
            contentType={LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[type]}
            coverImage={coverImage}
          />
          <div className="flex flex-1 grow flex-col hover:underline">
            <Text variant="compact-description" ellipsis>
              {name}
            </Text>
            <Text variant="fine-print-description">
              {CONTENT_TYPE_COPY[LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[type]]}
            </Text>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-start gap-2">
          <div className="flex justify-start">
            <Badge variant="success">
              <Text variant="compact-description">+{numAssignedSince}</Text>
            </Badge>
          </div>
          <Text variant="fine-print-description" className="text-gray-8">
            assigned last 7 days
          </Text>
        </div>
        <div className="flex flex-1 flex-row items-center gap-2">
          <Progress value={progress} className="bg-blue-1" />
          <Text variant="compact-description">{progress}%</Text>
        </div>
      </div>
    </Link>
  );
};

gql`
  fragment RecentlyAssignedLibraryItem__Widget on RecentlyAssignedLibraryItem {
    id
    libraryItem {
      id
      coverImage {
        ...CoverImage
      }
      name {
        uuid
        en
      }
      type
    }
    numAssignedSince
    numAssignedTotal
    numCompletedTotal
  }
  query RecentlyAssignedLibraryItems($since: DateTime!) {
    RecentlyAssignedLibraryItems(input: { since: { value: $since } }) {
      ...RecentlyAssignedLibraryItem__Widget
    }
  }
`;
export default RecentAssignmentsWidget;
